import React, { useState, useEffect } from 'react';
import {
  TextField,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Save, X, KeyRound, Eye, EyeOff, RefreshCw, Copy } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@atoms/Button';
import { generatePassword } from '@utils/passwordGenerator';
import { trpc } from '@lib/trpc';
import { useAuth } from '@contexts/AuthContext';
import { AdminResetPasswordModal } from './AdminResetPasswordModal';

interface User {
  id: string;
  email: string;
  username: string;
  name: string | null;
  isAdmin: boolean;
  isActive: boolean;
  roles?: Array<{
    id: string;
    name: string;
    displayName: string;
    color: string | null;
  }>;
}

interface AdminUserModalProps {
  user: User | null;
  isEditing: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const AdminUserModal: React.FC<AdminUserModalProps> = ({
  user,
  isEditing,
  onClose,
  onSuccess,
}) => {
  const { user: currentUser } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    name: '',
    password: '',
    isAdmin: false,
    isActive: true,
  });
  
  const [selectedRole, setSelectedRole] = useState<string>('');

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [lastCopyAction, setLastCopyAction] = useState<string>('');

  // Get tRPC utils for query invalidation
  const utils = trpc.useUtils();

  // Fetch available roles
  const { data: rolesData } = trpc.admin.getAllRoles.useQuery();
  const availableRoles = rolesData?.data || [];

  useEffect(() => {
      if (isEditing && user) {
        setFormData({
          email: user.email,
          username: user.username,
          name: user.name || '',
          password: '', // Don't pre-fill password for editing
          isAdmin: user.isAdmin,
          isActive: user.isActive,
        });
      setSelectedRole(user.roles?.[0]?.id || '');
    } else {
      // Reset form for creating new user and generate initial password
      const initialPassword = generatePassword();
      setFormData({
        email: '',
        username: '',
        name: '',
        password: initialPassword,
        isAdmin: false,
        isActive: true,
      });
      setSelectedRole('');
    }
  }, [isEditing, user]);

  const createUserMutation = trpc.admin.createUser.useMutation({
    onSuccess: (data) => {
      if (data.temporaryPassword) {
        setTemporaryPassword(data.temporaryPassword);
        // Don't show success message yet - wait for credentials modal
        // Don't invalidate queries or call onSuccess yet - we'll do that after showing credentials
      } else {
        toast.success('User created successfully');
        // Only invalidate and call onSuccess for non-temporary password cases
        utils.admin.getAllUsers.invalidate();
        onSuccess();
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create user');
    },
  });

  const updateUserMutation = trpc.admin.updateUser.useMutation({
    onSuccess: () => {
      toast.success('User updated successfully', { id: 'admin-user-updated' });
      // Invalidate user queries to refresh data everywhere
      utils.admin.getAllUsers.invalidate();
      onSuccess();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update user');
    },
  });

  // Mutations for role management
  const assignRoleMutation = trpc.admin.assignUserRole.useMutation();
  const removeRoleMutation = trpc.admin.removeUserRole.useMutation();

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submitData = {
      mode: 'generate' as const,
      ...formData,
      name: formData.name.trim() || undefined,
    };

    try {
      if (isEditing && user) {
        // Update user basic info first
        await updateUserMutation.mutateAsync({
          id: user.id,
          data: {
            email: submitData.email,
            username: submitData.username,
            name: submitData.name,
            isActive: submitData.isActive,
            // isAdmin excluded - managed directly in database
          },
        });

        // Handle role changes if roles were modified
        const currentRoleIds = user.roles?.map(role => role.id) || [];
        const selectedRoles = selectedRole ? [selectedRole] : [];
        
        // For single role selection, we replace all existing roles with the selected one
        // This handles cases where users might have multiple roles from before
        
        // Check if we need to make changes
        const hasExactlyOneCurrentRole = currentRoleIds.length === 1 && currentRoleIds[0] === selectedRole;
        const hasNoRolesAndNoneSelected = currentRoleIds.length === 0 && !selectedRole;
        const needsNoChange = hasExactlyOneCurrentRole || hasNoRolesAndNoneSelected;
        
        let rolesToRemove: string[] = [];
        let rolesToAdd: string[] = [];
        
        if (!needsNoChange) {
          // Always remove all current roles to ensure single role constraint
          rolesToRemove = currentRoleIds;
          // Add the new selected role (if any)
          rolesToAdd = selectedRoles;
        }

        // Remove roles that are no longer selected
        for (const roleId of rolesToRemove) {
          await removeRoleMutation.mutateAsync({
            userId: user.id,
            roleId: roleId.toString(),
          });
        }

        // Add newly selected roles
        for (const roleId of rolesToAdd) {
          await assignRoleMutation.mutateAsync({
            userId: user.id,
            roleId: roleId.toString(),
          });
        }

        // Show appropriate success message
        if (rolesToAdd.length > 0 || rolesToRemove.length > 0) {
          toast.success('User and roles updated successfully');
        } else {
          toast.success('User updated successfully', { id: 'admin-user-roles-updated' });
        }
        
      } else {
        // Create new user
        const createInput = formData.password
          ? {
              mode: 'manual' as const,
              email: submitData.email,
              username: submitData.username,
              name: submitData.name,
              password: formData.password,
              isActive: submitData.isActive,
              // isAdmin excluded - managed directly in database
            }
          : {
              mode: 'generate' as const,
              email: submitData.email,
              username: submitData.username,
              name: submitData.name,
              isActive: submitData.isActive,
              // isAdmin excluded - managed directly in database
            };
        const newUser = await createUserMutation.mutateAsync(createInput as any);

        // Only proceed with role assignment if we have a valid user and temporary password
        if (newUser.data?.id) {
          // Assign initial role if selected
          if (selectedRole) {
            await assignRoleMutation.mutateAsync({
              userId: newUser.data.id,
              roleId: selectedRole.toString(),
            });
          }

          // Only update data if we're not showing credentials
          if (!newUser.temporaryPassword) {
            utils.admin.getAllUsers.invalidate();
            toast.success('User created and roles assigned successfully');
            onSuccess();
          }
          // If we have a temporary password, the credentials modal will handle
          // invalidation and success callback when user clicks "Done"
        }
      }
      
    } catch (error) {
      // Error handling is done by individual mutations
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Show credentials screen if user was created
  if (temporaryPassword && !isEditing) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">✅ User Created Successfully</h2>
            
            <div className="bg-info-50 border border-info-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-info-800 mb-3">
                Please share these credentials with <strong>{formData.name || formData.username}</strong>.
                They will be required to change the password on first login.
              </p>
            </div>

            {/* Email */}
            <div className="mb-4">
              <p className="text-xs text-gray-600 mb-1">Email</p>
              <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded p-3">
                <p className="font-mono text-base text-gray-900">{formData.email}</p>
                <Button
                  size="sm"
                  variant="ghost"
                  icon={Copy}
                  onClick={() => {
                    const actionKey = 'email-copy';
                    if (lastCopyAction === actionKey) return;
                    setLastCopyAction(actionKey);
                    navigator.clipboard.writeText(formData.email);
                    toast.success('Email copied!', { id: 'email-copied' });
                    setTimeout(() => setLastCopyAction(''), 1000);
                  }}
                />
              </div>
            </div>

            {/* Password */}
            <div className="mb-6">
              <p className="text-xs text-gray-600 mb-1">Temporary Password</p>
              <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded p-3">
                <p className="font-mono text-base text-gray-900">{temporaryPassword}</p>
                <Button
                  size="sm"
                  variant="ghost"
                  icon={Copy}
                  onClick={() => {
                    const actionKey = 'password-copy';
                    if (lastCopyAction === actionKey) return;
                    setLastCopyAction(actionKey);
                    navigator.clipboard.writeText(temporaryPassword);
                    toast.success('Password copied!', { id: 'password-copied' });
                    setTimeout(() => setLastCopyAction(''), 1000);
                  }}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => {
                  const actionKey = 'credentials-copy';
                  if (lastCopyAction === actionKey) return;
                  setLastCopyAction(actionKey);
                  const credentials = `Email: ${formData.email}\nPassword: ${temporaryPassword}`;
                  navigator.clipboard.writeText(credentials);
                  toast.success('All credentials copied!', { id: 'credentials-copied' });
                  setTimeout(() => setLastCopyAction(''), 1000);
                }}
                icon={Copy}
                className="flex-1"
              >
                Copy Both
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  // Refresh user list and close modal
                  utils.admin.getAllUsers.invalidate();
                  setTemporaryPassword(null);
                  setLastCopyAction('');
                  onSuccess();
                }}
                className="flex-1"
              >
                Done
              </Button>
            </div>

            <div className="mt-4 p-3 bg-warning-50 rounded-lg">
              <p className="text-xs text-warning-700 text-center">
                ⚠️ The user must change this password on their first login
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isLoading = createUserMutation.isPending || updateUserMutation.isPending || assignRoleMutation.isPending || removeRoleMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <Typography variant="h6" component="h2" className="text-gray-900">
              {isEditing ? 'Edit User' : 'Create User'}
            </Typography>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              icon={X}
              onClick={onClose}
              disabled={isLoading}
              className="text-gray-400 hover:text-gray-600"
            />
          </div>

          {isEditing && (
            <div className="px-6 pt-2 -mb-2">
              <Button
                type="button"
                variant="secondary"
                icon={KeyRound}
                onClick={() => setIsResetPasswordModalOpen(true)}
                disabled={isLoading}
                className="w-full"
              >
                Reset User Password
              </Button>
            </div>
          )}

          {/* Reset Password Modal */}
          {user && (
            <AdminResetPasswordModal
              isOpen={isResetPasswordModalOpen}
              onClose={() => setIsResetPasswordModalOpen(false)}
              onSuccess={() => {
                // Intentionally empty to prevent any side effects
              }}
              userId={user.id}
              userName={user.name || user.username}
              userEmail={user.email}
            />
          )}

          {/* Form Content */}
          <Box className="p-6 space-y-4">
            {/* Email Field */}
            <TextField
              label="Email"
              type="email"
              fullWidth
              required
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              disabled={isLoading}
              error={!!errors.email}
              helperText={errors.email}
              variant="outlined"
              size="small"
            />

            {/* Username Field */}
            <TextField
              label="Username"
              type="text"
              fullWidth
              required
              value={formData.username}
              onChange={(e) => handleInputChange('username', e.target.value)}
              disabled={isLoading}
              error={!!errors.username}
              helperText={errors.username}
              variant="outlined"
              size="small"
            />

            {/* Full Name Field */}
            <TextField
              label="Full Name"
              type="text"
              fullWidth
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              disabled={isLoading}
              variant="outlined"
              size="small"
            />

            {/* Password Field */}
            {!isEditing && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <TextField
                      type={showPassword ? 'text' : 'password'}
                      fullWidth
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      disabled={isLoading}
                      placeholder="Generated password will appear here"
                      variant="outlined"
                      size="small"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      disabled={isLoading}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    icon={RefreshCw}
                    onClick={() => {
                      const newPassword = generatePassword();
                      handleInputChange('password', newPassword);
                      toast.success('New password generated!');
                    }}
                    disabled={isLoading}
                    className="whitespace-nowrap"
                  >
                    Generate
                  </Button>
                </div>
                <div className="bg-info-50 border border-info-200 rounded p-3">
                  <Typography variant="body2" className="text-info-700">
                    Click "Generate" to create a secure password, or enter a custom one.
                    The user will be required to change it on their first login.
                  </Typography>
                </div>
              </div>
            )}

            {/* Role Selection */}
            <FormControl fullWidth size="small">
              <InputLabel>Role</InputLabel>
              <Select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value as string)}
                label="Role"
                disabled={isLoading}
              >
                <MenuItem value="">
                  <em>No role selected</em>
                </MenuItem>
                {availableRoles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {role.color && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: role.color,
                          }}
                        />
                      )}
                      {role.displayName}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" className="text-gray-500 mt-1">
                {isEditing ? 'Select the primary role for this user' : 'You can assign a role here or add it later'}
              </Typography>
            </FormControl>

            {/* Checkboxes */}
            <Box className="space-y-2">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isActive}
                    onChange={(e) => handleInputChange('isActive', e.target.checked)}
                    disabled={isLoading}
                    size="small"
                  />
                }
                label="Active user"
              />
              {/* Administrator privileges hidden - managed directly in database */}
            </Box>
          </Box>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              icon={X}
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              icon={Save}
              loading={isLoading}
              disabled={isLoading}
            >
              {isEditing ? 'Update' : 'Create'} User
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
